/** Sozlamalar (demo `Settings`, `wipeSheet`): tillar, usul, maqsad, test vositalari, progressni tozalash. Stek ekrani. */
import { useState, type CSSProperties } from 'react';
import { LANG, LN } from '../data';
import type { Course } from '../data/types';
import { useStore, S, started, switchCourse, type Mode } from '../store';
import { nav } from '../lib/nav';
import { toast } from '../lib/toast';
import { t, tn, CONFIRM_WORD } from '../i18n';
import { fire, sync } from '../lib/sync';
import { Ic } from '../ui/Icon';
import { SheetBox } from '../ui/Sheet';
import { LEVELS, UiBtns } from './Onboarding';
import { GOAL_MIN, GOAL_MAX, clampGoal } from './lesson/engine';

const pill = (on: boolean): CSSProperties => ({
  height: 32, padding: '0 12px', fontSize: 13,
  background: on ? 'var(--primary)' : 'var(--primary-soft)', color: on ? 'var(--on-primary)' : 'var(--primary-ink)',
});

const LANGS: Array<[Course, string]> = [['en', 'Ingliz'], ['ru', 'Rus']];
const MODES: Array<[Mode, string]> = [['free', 'Mustaqil'], ['center', 'Oʻquv markazi']];

/** Demo `wipeSheet` — tasodifiy bosishdan himoya: soʻzni yozib tasdiqlash. */
export function WipeSheet({ onClose }: { onClose: () => void }) {
  const [v, setV] = useState('');
  const xp = useStore((s) => s.xp);
  const ok = v.trim().toUpperCase() === CONFIRM_WORD;
  const yes = () => {
    if (!ok) return;
    useStore.getState().wipe();
    onClose(); nav.home('home'); toast(t('Progress tozalandi'));
  };
  return (
    <div className="lsh" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <SheetBox title={t('Progressni tozalash')} titleColor="var(--error-ink)" onClose={onClose}>
        <span className="t13">{tn(`${LN.name} boʻyicha # ta soʻz, # XP va streak oʻchiriladi. Tasdiqlash uchun pastga`, started().length, xp)} <b>{CONFIRM_WORD}</b> {t('deb yozing.')}</span>
        <div className="field"><input autoComplete="off" autoCapitalize="characters" placeholder={CONFIRM_WORD} value={v} onChange={(e) => setV(e.target.value)} /></div>
        <span className="row g10">
          <button className="btn soft" style={{ height: 46 }} onClick={onClose}>{t('Bekor qilish')}</button>
          <button className="btn" style={{ height: 46, background: 'var(--error-strong)', color: '#fff' }} disabled={!ok} onClick={yes}>{t('Oʻchirish')}</button>
        </span>
      </SheetBox>
    </div>
  );
}

export function Settings() {
  const mode = useStore((s) => s.mode);
  const goal = clampGoal(useStore((s) => s.goal));
  const offset = useStore((s) => s.offset);
  const lvl = useStore((s) => s.lvl);
  const [wipe, setWipe] = useState(false);

  const setGoal = (n: number) => {
    if (n < GOAL_MIN || n > GOAL_MAX || n === goal) return;
    S().set({ goal: n });
    fire(sync.course({ daily_goal: n }), 'goal');
  };

  const setMode = (m: Mode) => {
    S().set({ mode: m, survey: { ...S().survey, learn: m } });
    fire(sync.me({ mode: m }), 'mode');
    toast(m === 'center' ? t('Darslar kartochkalaringizdan boradi') : t('Soʻzlar tasodifiy tartibda beriladi'));
  };

  return (
    <>
      <div className="sbar">
        <button className="x" aria-label={t('Orqaga')} onClick={() => nav.pop()}><Ic n="back" /></button>
        <b style={{ flex: 1, textAlign: 'left', color: 'var(--ink)', fontSize: 16 }}>{t('Sozlamalar')}</b>
      </div>
      <div className="sbody fade" style={{ gap: 16 }}>
        <div className="col g8">
          <span className="cap">{t('Oʻrganish')}</span>
          <div className="menu set2">
            <div><span>{t('Ilova tili')}</span><UiBtns /></div>
            <div><span>{t('Oʻrganilayotgan til')}</span><span className="row g6">{LANGS.map(([k, l]) => <button key={k} className="pill" style={pill(LANG === k)} onClick={() => { if (k === LANG) return; switchCourse(k); }}>{t(l)}</button>)}</span></div>
            <div><span>{t('Oʻrganish usuli')}</span><span className="row g6">{MODES.map(([k, l]) => <button key={k} className="pill" style={pill(mode === k)} onClick={() => setMode(k)}>{t(l)}</button>)}</span></div>
            <div><span>{t('Til darajasi')}</span><span className="row g6">{LEVELS.map(([k]) => <button key={k} className="pill" style={pill(lvl === k)} onClick={() => { S().set({ lvl: k }); fire(sync.course({ level: k }), 'level'); }}>{k}</button>)}</span></div>
            <div><span>{t('Kunlik maqsad (yangi soʻz)')}</span><span className="row g6" style={{ alignItems: 'center' }}>
              <button className="pill" style={pill(false)} aria-label="−" disabled={goal <= GOAL_MIN} onClick={() => setGoal(goal - 1)}>−</button>
              <b style={{ minWidth: 24, textAlign: 'center', color: 'var(--ink)' }}>{goal}</b>
              <button className="pill" style={pill(false)} aria-label="+" disabled={goal >= GOAL_MAX} onClick={() => setGoal(goal + 1)}>+</button>
            </span></div>
          </div>
        </div>
        <div className="col g8">
          <span className="cap">{t('Test vositalari')}</span>
          <div className="menu">
            <button onClick={() => { S().set({ offset: (offset || 0) + 1 }); fire(sync.skipDay(), 'skip-day'); toast(t('Bir kun oldinga surildi')); }}><span className="row g10"><Ic n="cal" size={20} style={{ color: 'var(--muted)' }} />{t('Ertangi kunga oʻtish')}</span><span className="t12">{offset ? tn('+# kun', offset) : t('test')}</span></button>
            {offset > 0 && <button onClick={() => { S().set({ offset: 0 }); fire(sync.me({ day_offset: 0 }), 'day-offset'); }}><span>{t('Haqiqiy sanaga qaytish')}</span><Ic n="chevRight" size={20} style={{ color: 'var(--muted)' }} /></button>}
          </div>
        </div>
        <details className="danger">
          <summary><span className="row g10"><Ic n="lock" size={18} />{t('Maʼlumotlarni boshqarish')}</span><Ic n="chevDown" size={18} /></summary>
          <div className="col g10" style={{ padding: '0 16px 16px' }}>
            <span className="t13">{t(`${LN.name} boʻyicha butun progress (soʻzlar, XP, streak, kartochkalar) oʻchiriladi. Buni qaytarib boʻlmaydi.`)}</span>
            <button className="btn soft" style={{ height: 46, color: 'var(--error-ink)', background: 'var(--error-soft)' }} onClick={() => setWipe(true)}><Ic n="trash" size={18} />{t('Progressni tozalash')}</button>
          </div>
        </details>
        <p className="t12" style={{ margin: 0 }}>{t('Progress Lexo hisobingizda saqlanadi. “Ertangi kunga oʻtish” keyingi kun darsini kutmasdan sinash uchun.')}</p>
      </div>
      {wipe && <WipeSheet onClose={() => setWipe(false)} />}
    </>
  );
}
